/**
 * Light language packs — behavior + UI keyed to an aspect-key CONVENTION.
 *
 * Aspects already work for any key with zero registration (they enter the model
 * as freeform `aspect key: value` pairs), so a pack does NOT invent a field
 * type, extend the grammar, or declare typed aspects with defaults. It brings:
 *
 *   - `procedures` — model checks keyed to a known aspect key, run in the
 *     checker/LSP diagnostics pass, surfacing as diagnostics (this module).
 *   - `docs` — completion/hover text for the key, so the convention is
 *     discoverable (consumed by the LSP providers).
 *   - `controls` — a first-class inspector editor for the key. React-typed, so
 *     it lives in the app-shell layer's pack wrapper, NOT here; the engine pack
 *     is the pure half both realms share.
 *
 * The registry is a process-global the host opts into (the LSP server registers
 * the packs its product ships). It's resettable for tests.
 */
import type { Diagnostic, Value } from "./types.js";
import type { ResolvedModel } from "./resolver.js";
import { buildNodeUniverse, type ElementNode, type NodeUniverse } from "./selector-eval.js";

/** What a {@link ProcedureDef} sees: the cascaded model plus its materialized
 *  node universe (each element with its resolved aspects), so a procedure reads
 *  `node.aspects.get("slo.p95")` without rebuilding the universe per pack. */
export interface ProcedureContext {
  readonly model: ResolvedModel;
  readonly universe: NodeUniverse;
}

/** A procedure — a model check keyed to an aspect-key convention. `aspectKey`
 *  is the convention it governs (e.g. `"slo"`); it documents intent and lets a
 *  host reason about which key a check belongs to. `run` decides what it flags
 *  and yields diagnostics anchored at the offending element's declaration. */
export interface ProcedureDef {
  readonly id: string;
  readonly aspectKey: string;
  run(ctx: ProcedureContext): readonly Diagnostic[];
}

/** Completion/hover documentation for one aspect key (e.g. `"slo.p95"`). */
export interface AspectDoc {
  /** The dotted aspect key this documents. */
  readonly key: string;
  /** One-line summary — shown as the completion detail / hover title. */
  readonly summary: string;
  /** Optional longer markdown body — shown in the completion doc / hover. */
  readonly detail?: string;
}

/** A light language pack (the pure half — see the module doc for `controls`). */
export interface LanguagePack {
  readonly id: string;
  readonly procedures?: readonly ProcedureDef[];
  readonly docs?: readonly AspectDoc[];
}

const registry = new Map<string, LanguagePack>();

/** Register (or replace, by id) a language pack. Called by the host at boot. */
export function registerLanguagePack(pack: LanguagePack): void {
  registry.set(pack.id, pack);
}

/** All registered packs, in registration order. */
export function languagePacks(): readonly LanguagePack[] {
  return [...registry.values()];
}

/** Drop all registered packs — test hygiene between cases. */
export function clearLanguagePacks(): void {
  registry.clear();
}

/**
 * Run every registered pack's procedures over the cascaded model and collect
 * their diagnostics. A procedure that throws (an un-evaluable check) is skipped
 * rather than crashing the whole pass — same tolerance the policy pass uses.
 * Returns `[]` fast when no procedures are registered (the common case).
 */
export function procedureDiagnostics(model: ResolvedModel): Diagnostic[] {
  const procedures = languagePacks().flatMap((p) => p.procedures ?? []);
  if (procedures.length === 0) return [];
  const ctx: ProcedureContext = { model, universe: buildNodeUniverse(model) };
  const out: Diagnostic[] = [];
  for (const proc of procedures) {
    try {
      out.push(...proc.run(ctx));
    } catch {
      /* un-evaluable procedure → contributes nothing */
    }
  }
  return out;
}

/** All aspect-key docs across registered packs. */
export function aspectDocs(): readonly AspectDoc[] {
  return languagePacks().flatMap((p) => p.docs ?? []);
}

/** The doc for one aspect key, if any pack registered it (first-registration
 *  wins on a duplicate key). */
export function aspectDocFor(key: string): AspectDoc | undefined {
  for (const pack of registry.values()) {
    for (const doc of pack.docs ?? []) if (doc.key === key) return doc;
  }
  return undefined;
}

/** Read a single numeric aspect value off an element (first value wins), or
 *  `undefined` when absent / non-numeric. A shared helper for numeric-budget
 *  procedures. */
export function numericAspect(node: ElementNode, key: string): number | undefined {
  const v: Value | undefined = node.aspects.get(key)?.[0];
  return v && v.kind === "number" ? v.value : undefined;
}
